
import React from 'react';
import { FileText, CheckCircle, Ship, Download, Plus, Search } from 'lucide-react';
import StatCard from '../components/StatCard';
import QuickActionButton from '../components/QuickActionButton';
import type { HistoryEntry, View } from '../types';

interface DashboardProps {
  history: HistoryEntry[];
  setCurrentView: (view: View) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ history, setCurrentView }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-3xl font-bold text-white mb-2">Welcome back, Economic Operator</h2>
        <p className="text-gray-400">Manage your customs declarations efficiently</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Active Declarations" value={24} Icon={FileText} change="+12%" changeColor="text-teal-400 bg-teal-900/50" iconBgColor="from-teal-500 to-teal-600" />
        <StatCard title="Completed This Month" value={156} Icon={CheckCircle} change="+8%" changeColor="text-blue-400 bg-blue-900/50" iconBgColor="from-blue-500 to-blue-600" />
        <StatCard title="Pending Review" value={8} Icon={Ship} change="2 new" changeColor="text-amber-400 bg-amber-900/50" iconBgColor="from-amber-500 to-amber-600" />
        <StatCard title="XML Generated" value={142} Icon={Download} change="98%" changeColor="text-emerald-400 bg-emerald-900/50" iconBgColor="from-emerald-500 to-emerald-600" />
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickActionButton 
            title="New Declaration" 
            subtitle="Upload documents" 
            Icon={Plus}
            onClick={() => setCurrentView('upload')}
            colorClasses={{
              border: 'hover:border-teal-500',
              bg: 'hover:bg-teal-900/50',
              iconBg: 'group-hover:bg-teal-800',
              iconText: 'group-hover:text-teal-400',
            }}
          />
          <QuickActionButton 
            title="Search Declarations" 
            subtitle="Find past entries" 
            Icon={Search}
            onClick={() => setCurrentView('history')}
            colorClasses={{
              border: 'hover:border-blue-500',
              bg: 'hover:bg-blue-900/50',
              iconBg: 'group-hover:bg-blue-800',
              iconText: 'group-hover:text-blue-400',
            }}
          />
          <QuickActionButton 
            title="Batch Export" 
            subtitle="Download multiple XMLs" 
            Icon={Download}
            onClick={() => {}}
            colorClasses={{
              border: 'hover:border-purple-500',
              bg: 'hover:bg-purple-900/50',
              iconBg: 'group-hover:bg-purple-800',
              iconText: 'group-hover:text-purple-400',
            }}
          />
        </div>
      </div>

      <div className="glass-card rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {history.slice(0, 5).map((item, index) => (
            <div key={item.id} className="flex items-center justify-between p-4 bg-gray-800 rounded-lg border border-gray-700 hover:border-teal-500 transition-all" style={{ animationDelay: `${index * 100}ms` }}>
              <div className="flex items-center gap-4">
                <div className="p-2 bg-teal-900/50 rounded-lg">
                  <FileText className="text-teal-400" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-white">{item.reference}</p>
                  <p className="text-sm text-gray-400">{item.importer}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400">{item.date}</span>
                <span className="px-3 py-1 bg-emerald-900/50 text-emerald-400 text-xs font-semibold rounded-full">
                  Completed
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
